import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import Select from 'react-select';
import { ChatBot } from "../../services/dashboard/GetChatBots";
import "../../styles/dash/CreateOrEditChatBot.css";
import { EditOrCreateChatBot, SendChatBot } from "../../services/dashboard/EditOrCreateChatBot";
import LoadingSpinner from "../utility/LoadingSpinner";
import { GetClientes, Cliente, GetAgentes, Agente } from "../../services/dashboard/GetClientes";

interface Props {
  bot: ChatBot | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ApiError {
  response?: {
    data?: string;
  };
}

interface ClientOption {
  value: number;
  label: string;
}

const CreateOrEditChatBot: React.FC<Props> = ({ bot, onClose, onSuccess }) => {
  const [client, setClient] = useState<ClientOption | null>(null);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [agent, setAgent] = useState<ClientOption | null>(null);
  const [agentOptions, setAgentOptions] = useState<ClientOption[]>([]);
  const [botKey, setBotKey] = useState("");
  const [botName, setBotName] = useState("");
  const [color, setColor] = useState("#25d366");
  const [iconName, setIconName] = useState("");
  const [iconBase64, setIconBase64] = useState("");
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [mensajeChat, setMensajeChat] = useState(""); // Nuevo estado
  const [allowedDomains, setAllowedDomains] = useState(""); // Nuevo estado
  const [redFace, setRedFace] = useState(""); // Nuevo estado
  const [redInstagran, setRedInstagran] = useState(""); // Nuevo estado
  const [redGmail, setRedGmail] = useState(""); // Nuevo estado

  // TODO: Replace with the correct environment variable for chatbot image URLs.
  const URL_BACK_IMG = process.env.REACT_APP_API_URL_BOT_IMG || '/chatbot-images/';

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData: Cliente[] = await GetClientes();
        const options = clientsData.map(c => ({ value: c.id, label: c.nombre }));
        setClientOptions(options);
      } catch (error) {
        setErrorMessage("No se pudieron cargar los clientes.");
        console.error("Error fetching clients:", error);
      }
    };

    const fetchAgents = async () => {
      try {
        const agentsData: Agente[] = await GetAgentes();
        const options = agentsData.map(a => ({ value: a.id, label: a.nombre }));
        setAgentOptions(options);
      } catch (error) {
        setErrorMessage("No se pudieron cargar los agentes.");
        console.error("Error fetching agents:", error);
      }
    };
    fetchClients();
    fetchAgents();
  }, []);

  const handleClose = () => {
    setAnimateOut(true);
    setTimeout(() => {
      onClose();
      setAnimateOut(false);
    }, 300);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrorMessage("La imagen no puede superar los 5MB.");
        return;
      }
      setIconName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setIconBase64(base64String);
        setIconPreview(base64String);
        setErrorMessage("");
      };
      reader.onerror = () => {
        setErrorMessage("Error al leer la imagen.");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitEditcreate = async () => {
    setLoading(true);
    setErrorMessage("");

    if (!client || client.value === 0) {
      setErrorMessage("Debe seleccionar un cliente.");
      setLoading(false);
      return;
    }

    try {
      const chatBotData: EditOrCreateChatBot = {
        IDChatbot: bot ? bot.id : 0,
        IDCliente: client ? client.value : 0,
        IDAgente: agent ? agent.value : 0,
        Key: botKey,
        Nombre: botName,
        Color: color,
        Icono: iconBase64 || (bot ? bot.icono : ""),
        IconoName: iconName,
        Status: isActive ? 1 : 0,
        mensajeChat: mensajeChat, // Añadir nuevo campo
        AllowedDomains: allowedDomains, // Añadir nuevo campo
        redFace: redFace, // Añadir nuevo campo
        redInstagran: redInstagran, // Añadir nuevo campo
        redGmail: redGmail, // Añadir nuevo campo
      };

      const response = await SendChatBot(chatBotData);

      if (response.status === 200) {
        onSuccess();
        handleClose();
      } else {
        setErrorMessage(response.message);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      setErrorMessage(apiError.response?.data || "Error al enviar el ChatBot");
      console.error("Error al enviar el ChatBot:", error);
    } finally {
      setLoading(false);
    }
  };

  const URL_BACK_IMGBOT = process.env.REACT_APP_API_IMGBOT_URL;

  useEffect(() => {
    if (bot) {
      const clientOption = clientOptions.find(option => option.value === bot.idCliente);
      const agentOption = agentOptions.find(option => option.value === bot.idAgente);
      setClient(clientOption || null);
      setAgent(agentOption || null);
      setBotKey(bot.key || "");
      setBotName(bot.nombre || "");
      setColor(bot.color || "#25d366");
      setIconName(bot.icono || "");
      setIconBase64("");
      setIconPreview(null);
      setIsActive(bot.status === 1);
      setMensajeChat(bot.mensajeChat || ""); // Inicializar nuevo estado
      setAllowedDomains(bot.allowedDomains || ""); // Inicializar nuevo estado
      setRedFace(bot.redFace || ""); // Inicializar nuevo estado
      setRedInstagran(bot.redInstagran || ""); // Inicializar nuevo estado
      setRedGmail(bot.redGmail || ""); // Inicializar nuevo estado
    } else {
      setClient(null);
      setAgent(null);
      setBotKey("");
      setBotName("");
      setColor("#25d366");
      setIconName("");
      setIconBase64("");
      setIconPreview(null);
      setIsActive(true);
      setMensajeChat(""); // Resetear nuevo estado
      setAllowedDomains(""); // Resetear nuevo estado
      setRedFace(""); // Resetear nuevo estado
      setRedInstagran(""); // Resetear nuevo estado
      setRedGmail(""); // Resetear nuevo estado
    }
  }, [bot, clientOptions, agentOptions]);

  return (
    <Modal
      show={!animateOut}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      contentClassName={`chatbot-form-modal ${animateOut ? 'modal-exit' : 'modal-enter'}`}
    >
      <Modal.Header closeButton closeVariant="white" className="border-0" />
      <Modal.Body className="chatbot-form-content">
        <h5 className="form-title text-center mb-4">
          {bot ? "Editar ChatBot" : "Crear ChatBot"}
        </h5>
        <Form>
          {loading && <LoadingSpinner />}
          <div className="form-grid">
            <div className="form-group">
              <label>Cliente</label>
              <Select
                options={clientOptions}
                value={client}
                onChange={(selectedOption) => setClient(selectedOption as ClientOption)}
                placeholder="Selecciona un cliente"
                className="basic-select"
                classNamePrefix="select"
                isLoading={clientOptions.length === 0}
                menuPortalTarget={document.body}
                menuPosition={"fixed"}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), control: (base) => ({ ...base, border: '1px solid #444444', borderRadius: '4px', minHeight: '38px', backgroundColor: '#2a2a2a', color: '#fff' }), menu: (base) => ({ ...base, backgroundColor: '#2a2a2a', color: '#fff' }), option: (base, { isFocused }) => ({ ...base, backgroundColor: isFocused ? '#2a2a2a' : '#1d1c1cff', color: '#fff' }), singleValue: (base) => ({ ...base, color: '#fff' }), placeholder: (base) => ({ ...base, color: '#aaa' }) }}
              />
            </div>
            <div className="form-group">
              <label>Agente</label>
              <Select
                options={agentOptions}
                value={agent}
                onChange={(selectedOption) => setAgent(selectedOption as ClientOption)}
                placeholder="Selecciona un agente"
                className="basic-select"
                classNamePrefix="select"
                isLoading={agentOptions.length === 0}
                menuPortalTarget={document.body}
                menuPosition={"fixed"}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), control: (base) => ({ ...base, border: '1px solid #444444', borderRadius: '4px', minHeight: '38px', backgroundColor: '#2a2a2a', color: '#fff' }), menu: (base) => ({ ...base, backgroundColor: '#2a2a2a', color: '#fff' }), option: (base, { isFocused }) => ({ ...base, backgroundColor: isFocused ? '#2a2a2a' : '#1d1c1cff', color: '#fff' }), singleValue: (base) => ({ ...base, color: '#fff' }), placeholder: (base) => ({ ...base, color: '#aaa' }) }}
              />
            </div>
            <div className="form-group">
              <label>Key del ChatBot</label>
              <input type="text" value={botKey} onChange={(e) => setBotKey(e.target.value)} placeholder="Ej: xxxxxxxx" />
            </div>
            <div className="form-group">
              <label>Nombre del ChatBot</label>
              <input type="text" value={botName} onChange={(e) => setBotName(e.target.value)} placeholder="Ej: LuzIA" />
            </div>
            <div className="form-group">
              <label>Color del ChatBot</label>
              <div className="color-picker-wrapper">
                <button type="button" className="color-button" style={{ backgroundColor: color }} onClick={() => setShowColorPicker(!showColorPicker)}>{color}</button>
                {showColorPicker && <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />}
              </div>
            </div>
            <div className="form-group">
              <label>Icono del ChatBot</label>
              <input type="file" id="iconUpload" accept="image/png, image/jpeg, image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
              <label htmlFor="iconUpload" className="file-upload-button">Seleccionar Imagen</label>
              {iconPreview ? (
                <img src={iconPreview} alt="Vista previa del ícono" className="image-preview" />
              ) : (
                bot?.icono && <img src={`${URL_BACK_IMGBOT}${bot.icono}`} alt="Ícono actual" className="image-preview" />
              )}
            </div>
            <div className="form-group switch-group">
              <label>¿Activo?</label>
              <label className="switch">
                <input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} />
                <span className="slider round"></span>
              </label>
            </div>
            {/* Nuevos campos */}
            <div className="form-group">
              <label>Mensaje de Chat</label>
              <input type="text" value={mensajeChat} onChange={(e) => setMensajeChat(e.target.value)} placeholder="Mensaje inicial del chat" />
            </div>
            <div className="form-group">
              <label>Dominios Permitidos</label>
              <textarea value={allowedDomains} onChange={(e) => setAllowedDomains(e.target.value)} placeholder="dominio1.com,dominio2.com"></textarea>
            </div>
            <div className="form-group">
              <label>Red Social Facebook</label>
              <input type="text" value={redFace} onChange={(e) => setRedFace(e.target.value)} placeholder="URL de Facebook" />
            </div>
            <div className="form-group">
              <label>Red Social Instagram</label>
              <input type="text" value={redInstagran} onChange={(e) => setRedInstagran(e.target.value)} placeholder="URL de Instagram" />
            </div>
            <div className="form-group">
              <label>Red Social Gmail</label>
              <input type="text" value={redGmail} onChange={(e) => setRedGmail(e.target.value)} placeholder="Correo electrónico de Gmail" />
            </div>
            {/* Fin de nuevos campos */}
            <div className="form-group full-width">
              {errorMessage && <div className="error-message" style={{ color: '#ff4d4f', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', padding: '8px 15px', borderRadius: '4px', marginBottom: '15px', textAlign: 'center', fontSize: '0.9rem' }}>{errorMessage}</div>}
              <button type="button" className="save-button" onClick={handleSubmitEditcreate} disabled={loading}>{loading ? "GUARDANDO..." : "GUARDAR CHATBOT"}</button>
            </div>
            <div className="form-group full-width text-center">
              <button type="button" className="cancel-button" onClick={handleClose} disabled={loading}>Cerrar</button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateOrEditChatBot;
